// Angular
import { CommonModule, NgClass } from '@angular/common';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NonNullableFormBuilder,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import {
  Component,
  forwardRef,
  inject,
  Input,
  OnInit,
  signal,
} from '@angular/core';
// PrimeNG
import { ButtonModule } from 'primeng/button';
// Pipes
import { FileSizePipe } from '@shared/pipes-v2/file-size.pipe';
// Models
import { FileData } from './models/file-data.interface';
import { ControlsOf } from '@shared/models-v3/controls-of.type';
import { FileUploadInputUtilsService } from './services/file-upload-input-utils/file-upload-input-utils.service';
import { FileValidator } from './validators/file.validator';

const NG_MODULES = [NgClass, CommonModule];
const PRIME_MODULES = [ButtonModule];
const PIPES = [FileSizePipe];

@Component({
  selector: 'app-file-upload-input',
  standalone: true,
  imports: [NG_MODULES, PRIME_MODULES, PIPES],
  templateUrl: './file-upload-input.component.html',
  styleUrl: './file-upload-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploadInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => FileUploadInputComponent),
      multi: true,
    },
  ],
})
export class FileUploadInputComponent implements OnInit, Validator {
  /**
   * Atributo `accept` del input de tipo `file`.
   * Este atributo especifica los tipos de archivos que el usuario puede seleccionar.
   * + Por defecto es `.pdf`.
   */
  @Input() accept: string = '.pdf';
  /**
   * Tamaño máximo de archivo permitido en bytes.
   * Por defecto es 1MB.
   */
  @Input() maxFileSize: number = 1024 * 1024 * 10;
  /**
   * Número máximo de archivos que se pueden cargar.
   * + Por defecto es 1.
   */
  @Input() fileLimit: number = 1;
  /**
   * Indica si el campo es requerido.
   */
  @Input() required: boolean = true;

  private onChange: (value: File[]) => void = () => {};
  private onTouch: () => void = () => {};

  isMultiple: boolean = false;
  acceptLabel: string = '';

  files = signal<File[]>([]);
  detailedFiles = signal<FileData[]>([]);
  isDragging = signal(false);
  form!: FormGroup;
  invalidFileError = signal<string>('');

  get filesField(): FormControl<File[]> {
    return this.form.get('files') as FormControl<File[]>;
  }

  private readonly fb = inject(NonNullableFormBuilder);

  ngOnInit(): void {
    this.isMultiple = this.fileLimit > 1;
    this.invalidFileError.set(
      this.isMultiple ? 'filesInvalid' : 'fileInvalid'
    );
    this.acceptLabel = FileUploadInputUtilsService.getAcceptLabel(this.accept);
    this.buildForm();
  }

  /**
   * Build the form group.
   * @private
   * @returns void
   **/
  private buildForm(): void {
    this.form = this.fb.group({
      files: [null, FileValidator.isValid],
    });
  }

  //#region File events
  /**
   * Maneja el evento de arrastrar sobre el componente.
   * @param event - El evento de arrastrar (DragEvent).
   * Este método previene el comportamiento predeterminado y la propagación del evento,
   * y establece el estado de arrastre a verdadero.
   */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(true);
  }

  /**
   * Maneja el evento de arrastrar y soltar cuando el elemento arrastrado sale del área de destino.
   * @param event - El evento de arrastrar y soltar.
   * Este método previene el comportamiento predeterminado del evento y detiene su propagación.
   * Además, establece la propiedad `isDragging` a `false` para indicar que el elemento ya no está siendo arrastrado sobre el área de destino.
   */
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  /**
   * Maneja el evento de soltar archivos en el área de carga.
   * @param event - El evento de arrastrar y soltar (DragEvent).
   * Este método previene el comportamiento predeterminado y la propagación del evento,
   * cambia el estado de arrastre a falso, y si hay archivos en el evento de transferencia de datos,
   * los añade a la lista de archivos.
   */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
    if (!event.dataTransfer) return;
    const { files } = event.dataTransfer;
    if (!files.length) return;
    this.addFiles(files);
  }

  /**
   * Maneja el evento cuando se selecciona un archivo en el input.
   * @param {Event} event - El evento de selección de archivo.
   * @returns {void}
   */
  onFileSelected(event: Event): void {
    const { files } = event.target as HTMLInputElement;
    if (!files) return;
    this.addFiles(files);
  }
  //#endregion

  /**
   * Añade archivos a la lista de archivos.
   * @param {FileList} fileList - Lista de archivos a añadir.
   * @returns {void}
   */
  addFiles(fileList: FileList): void {
    let files = Array.from(fileList);

    // NO HACE NADA:: Si se intenta añadir varios archivos cuando el límite es 1.
    if (!this.isMultiple && files.length > 1) return;
    if (this.isMultiple && this.files().length >= this.fileLimit) return;
    // QUITA EL DETALLE Y AGREGA UNO NORMAL::cuando existe algun detalle y el limite es 1.
    if (!this.isMultiple && this.detailedFiles().length)
      this.detailedFiles.set([]);
    // QUITA ARCHIVO::si solo se permite uno y ya hay uno.
    if (!this.isMultiple && this.files().length) {
      this.files.set([]);
      this.filesField.setValue(this.files());
    }

    if (this.isMultiple && files.length > this.fileLimit) {
      files = files.slice(0, this.fileLimit);
    }

    // QUITA DETALLE Y ARCHIVO::si se permiten muchos, pero ya hay algunos con la misma información. Se actualizan los archivos y los detalles.
    if (this.isMultiple && this.detailedFiles().length) {
      const newDetailedFiles = this.detailedFiles().filter(
        (file) => !this.exists(file, files)
      );
      if (newDetailedFiles.length !== this.detailedFiles().length)
        this.detailedFiles.set(newDetailedFiles);
      // Modificar los archivos quitando los que ya existian:
      const newFiles = this.files().filter(
        (file) => !this.exists(file, files)
      );
      if (newFiles.length !== this.files().length) {
        this.files.set(newFiles);
        this.filesField.setValue(this.files());
      }
    }

    // const newDetailedFiles: FileData[] = [];

    files.forEach((file) => {
      const fileSize = file.size;
      const isValidFileType = FileUploadInputUtilsService.isValidFileType(
        file,
        this.accept
      );
      const isValidFileSize = fileSize <= this.maxFileSize;
      const fileIcon = FileUploadInputUtilsService.getFileIcon(file.type);
      const fileData: FileData = {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        icon: fileIcon,
        messageError: null,
        hasError: false,
      };

      if (!isValidFileType) {
        fileData.hasError = true;
        fileData.messageError = 'Tipo de archivo no permitido.';
        this.filesField.setErrors({ [this.invalidFileError()]: true });
      } else if (!isValidFileSize) {
        fileData.hasError = true;
        fileData.messageError = 'Tamaño de archivo excedido.';
        this.filesField.setErrors({ [this.invalidFileError()]: true });
      } else {
        // Añadir archivo a la lista de archivos.
        this.files.update((prev) => [...prev, file]);
        this.filesField.setValue(this.files());
        const someInvalidFile = this.detailedFiles().some(
          (file) => file.hasError
        );
        if (someInvalidFile)
          this.filesField.setErrors({ [this.invalidFileError()]: true });
      }
      //this.filesField.updateValueAndValidity();
      this.onTouch();
      this.onChange(this.files());
      //newDetailedFiles.push(fileData);
      this.detailedFiles.update((prev) => [...prev, fileData]);
    });

    // Añadir detalle a lista de detalle de archivos.
    //this.detailedFiles.update((prev) => [...prev, ...newDetailedFiles]);
  }

  /**
   * Elimina un archivo de la lista de archivos.
   * @param index - Índice del archivo a eliminar.
   * @returns
   */
  removeFile(index: number): void {
    const detailedFile = this.detailedFiles()[index];
    // Eliminar archivo de la lista de detalles de archivos.
    this.detailedFiles.update((prev) => {
      prev.splice(index, 1);
      return prev;
    });

    // Si no tiene error lo busca y lo elimina de la lista de archivos.
    if (!detailedFile.hasError) {
      // Buscar archivo en la lista de archivos según el detalle.
      const fileIndex = this.files().findIndex(
        (file) =>
          file.name === detailedFile.name &&
          file.size === detailedFile.size &&
          file.type === detailedFile.type &&
          file.lastModified === detailedFile.lastModified
      );

      if (fileIndex >= 0) {
        // Eliminar archivo de la lista de archivos.
        this.files.update((prev) => {
          prev.splice(fileIndex, 1);
          return prev;
        });
      }
    }

    this.filesField.setValue(this.files());
    const someInvalidFile = this.detailedFiles().some((file) => file.hasError);
    //if (!someInvalidFile) this.filesField.setErrors(null);
    if (someInvalidFile)
      this.filesField.setErrors({ [this.invalidFileError()]: true });
    //this.filesField.updateValueAndValidity();
    this.onChange(this.files());
  }

  /**
   * Verifica si un archivo ya existe
   * @param file -
   * @returns
   */
  private exists<T extends File | FileData>(
    file: T,
    list: File[] | FileData[]
  ): boolean {
    return list.some(
      (f) =>
        f.name === file.name &&
        f.size === file.size &&
        f.type === file.type &&
        f.lastModified === file.lastModified
    );
  }

  // #region ControlValueAccessor
  /**
   * Actualizar el valor del input cuando el formulario padre cambia.
   * + Angular lo invoca automáticamente cuando se actualiza el valor del FormControl asociado con el componente.
   * + Es invocado automáticamente por Angular y no necesitas llamarlo manualmente.
   * + Tu implementación debería actualizar el valor del input en la UI con el valor proporcionado.
   * @param value The new value for the control.
   * @returns void
   * @example
   * writeValue(value: any): void {
   *   if (value) {
   *     this.form.get('documentId')?.setValue(value);
   *   }
   * }
   **/
  writeValue(value: File[]): void {
    if (!value) return;
    this.filesField.setValue(value);
  }

  /**
   * Registrar una función que debe ser invocada cada vez que el valor del input cambie.
   * + Esta función se utiliza para notificar al formulario padre que el valor del input ha cambiado.
   * + No se invoca directamente.
   * + Debes guardar la referencia de la función fn proporcionada y llamarla cuando el valor del input cambie.
   * @param fn The callback function.
   * @returns void
   * @example
   * registerOnChange(fn: any): void {
   *   this.onChange = fn;
   * }
   * onInput(event: Event): void {
   *   const value = (event.target as HTMLInputElement).value;
   *   this.onChange(value);  // Notificar al formulario padre
   * }
   **/
  registerOnChange(fn: (value: File[]) => void): void {
    this.onChange = fn;
  }

  /**
   * Registrar una función que debe ser invocada cuando el control es "tocado" (es decir, cuando el usuario interactúa con él).
   * + Se invoca cuando se necesita notificar que el control fue tocado, generalmente en eventos como blur.
   * @param fn The callback function.
   * @returns void
   * @example
   * registerOnTouched(fn: any): void {
   *   this.onTouched = fn;
   * }
   * onBlur(): void {
   *   this.onTouched();  // Notificar que el control fue tocado
   * }
   **/
  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }
  // #endregion

  // #region Validator
  /**
   * Validar el control síncronamente.
   * @returns The validation errors or null.
   * */
  validate(): ValidationErrors | null {
    return this.filesField.errors;
  }
  // #endregion
}
